import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import { useReactive } from 'ahooks';
import { Input, Button, Upload, message } from 'kenshin';
import {
  SendOutlined,
  PaperClipOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import {
  imageModels,
  imageQuantityOptions,
  videoModels,
  chatModels,
  sizeRatioToWidthHeight,
} from '../../config/models';
import { OSSImgUrl, uploadFileToOSS } from '@/_util/util';
import './index.less';

const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

// AI会话模型选项（使用文档中的模型）
const chatModelOptions = chatModels;

// AI视频时长选项
const videoDurationOptions = [
  { label: '4s', value: '4' },
  { label: '5s', value: '5' },
  { label: '6s', value: '6' },
  { label: '7s', value: '7' },
  { label: '8s', value: '8' },
  { label: '9s', value: '9' },
  { label: '10s', value: '10' },
  { label: '11s', value: '11' },
  { label: '12s', value: '12' },
  { label: '13s', value: '13' },
  { label: '14s', value: '14' },
  { label: '15s', value: '15' },
];

// gpt-5.5 质量选项
const gptQualityOptions = [
  { label: '低', value: 'low' },
  { label: '中', value: 'medium' },
  { label: '高', value: 'high' },
  { label: '自动', value: 'auto' },
];

// 下拉选择组件
const DropdownSelect = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div
      className="dropdownWrapper"
      ref={ref}
      style={{ display: options.length > 0 ? 'block' : 'none' }}
    >
      <div className="dropdownTrigger" onClick={() => setOpen(!open)}>
        {selected?.label || placeholder || options[0]?.label}
      </div>
      {open && (
        <div className="dropdownMenu">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`dropdownItem ${
                opt.value === value ? 'dropdownItemActive' : ''
              }`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="dropdownItemLabel">{opt.label}</span>
              {opt.value === value && (
                <CheckOutlined className="dropdownItemCheck" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InputArea = React.forwardRef((props, ref) => {
  const { mode, loading, onSend, onModeChange } = props;
  const textareaRef = useRef();
  const modeOptions = [
    { label: 'AI会话', value: MODES.CHAT },
    { label: 'AI生图', value: MODES.IMAGE },
    { label: 'AI视频', value: MODES.VIDEO },
  ];

  // 默认模型取 config/models.js 第一个
  const defaultImageModel = imageModels[0]?.value || '';
  const defaultVideoModel = videoModels[0]?.value || '';
  const defaultChatModel = chatModels[0]?.value || '';

  const state = useReactive({
    text: '',
    images: [], // 参考图预览（blob URL，用于显示）
    rawImages: [], // 参考图 File 对象（用于上传）
    // 生图参数
    imageModel: defaultImageModel,
    imageSize: '2K',
    imageRatio: '1:1',
    imageQuantity: imageQuantityOptions[0]?.value || 1,
    gptQuality: 'low', // gpt-5.5 质量参数
    // 视频参数
    videoModel: defaultVideoModel,
    videoRatio: '16:9',
    videoResolution: '720p',
    videoDuration: '5',
    // 聊天参数
    chatModel: defaultChatModel,
    // gpt-5.5 引用参数
    lastTaskId: null, // 引用的上一轮对话 itemId
    referenceImageUrl: null, // 引用图 URL（用于显示）
  });

  // 暴露方法给父组件调用
  useImperativeHandle(ref, () => ({
    // 添加参考图（来自 ImagePreview 的"添加到对话")
    addReferenceImage: (imageUrl) => {
      state.images = [...state.images, imageUrl];
    },
    // 设置引用图（来自 ChatArea 的"引用"按钮）
    setReferenceImage: (itemId, imageUrl) => {
      if (state.imageModel !== 'M-0003-0001') {
        message.error('仅gpt-5.5 模型支持引用功能，请切换当前模型。');
        return;
      }
      state.lastTaskId = itemId;
      state.referenceImageUrl = imageUrl;
      message.success('已引用该图片');
    },
    // 清除引用图
    clearReferenceImage: () => {
      state.lastTaskId = null;
      state.referenceImageUrl = null;
    },
    // 拖拽文件上传（供父组件调用）
    addDroppedFiles: (files) => {
      files.forEach((file) => handleUpload(file));
    },
    // 获取当前状态（供父组件查询）
    getState: () => ({
      lastTaskId: state.lastTaskId,
      referenceImageUrl: state.referenceImageUrl,
      gptQuality: state.gptQuality,
    }),
  }));

  // 发送消息
  const handleSend = async () => {
    if (
      !state.text.trim() &&
      state.images.length === 0 &&
      state.rawImages.length === 0
    )
      return;

    let params;
    let setting = undefined;
    let ossImageUrls = [];

    // 如果有未上传的图片，先上传到 OSS
    if (state.rawImages.length > 0) {
      message.loading({ content: '正在上传图片...', key: 'upload' });
      try {
        const uploadPromises = state.rawImages.map((file) =>
          uploadFileToOSS(file, '/ai-creation'),
        );
        const ossPaths = await Promise.all(uploadPromises);
        ossImageUrls = ossPaths.map((path) => OSSImgUrl(path));
        message.success({ content: '图片上传成功', key: 'upload' });
      } catch (err) {
        console.error('上传失败:', err);
        message.error({ content: '图片上传失败，请重试', key: 'upload' });
        return; // 上传失败则不发送
      }
    }

    // 合并已有的 OSS URL（来自添加到对话）和刚上传的 URL
    const allImageUrls = [
      ...state.images.filter((url) => url.startsWith('http')),
      ...ossImageUrls,
    ];

    if (mode === MODES.IMAGE) {
      const currentModel = imageModels.find(
        (m) => m.value === state.imageModel,
      );
      const { width, height } = sizeRatioToWidthHeight(
        state.imageSize,
        state.imageRatio,
      );
      params = {
        modelCode: state.imageModel,
        size: state.imageSize,
        width,
        height,
        quantity: state.imageQuantity,
      };

      // gpt-5.5 特殊配置
      if (currentModel?.hasSetting) {
        setting = {
          quality: state.gptQuality || 'low',
        };
        if (state.lastTaskId) {
          setting.lastTaskId = state.lastTaskId;
        }
      }
    } else if (mode === MODES.VIDEO) {
      params = {
        modelCode: state.videoModel,
        ratio: state.videoRatio,
        resolution: state.videoResolution,
        duration: state.videoDuration,
      };
    } else if (mode === MODES.CHAT) {
      params = {
        modelCode: state.chatModel,
      };
    }

    onSend({
      text: state.text,
      images: allImageUrls,
      mode,
      params,
      setting, // gpt-5.5 的特殊配置
    });

    // 发送后清空输入
    state.text = '';
    state.images = [];
    state.rawImages = [];
    state.referenceImageUrl = null;
    state.lastTaskId = null;
  };

  // 选择图片（只存储 File 对象，不上传）
  const handleUpload = (file) => {
    // 生成 blob URL 用于预览
    const previewUrl = URL.createObjectURL(file);
    state.images = [...state.images, previewUrl];
    state.rawImages = [...state.rawImages, file];
    return false; // 阻止默认上传行为
  };

  // 移除图片
  const removeImage = (index) => {
    // 释放 blob URL
    const previewUrl = state.images[index];
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    state.images = state.images.filter((_, i) => i !== index);
    state.rawImages = state.rawImages.filter((_, i) => i !== index);
  };

  // 键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 渲染AI会话输入区
  const renderChatInput = () => (
    <div className="paramRow">
      <DropdownSelect
        options={modeOptions}
        value={mode}
        onChange={(v) => onModeChange?.(v)}
      />
      <DropdownSelect
        options={chatModelOptions}
        value={state.chatModel}
        onChange={(v) => (state.chatModel = v)}
      />
    </div>
  );

  const renderUpload = () => (
    <div className="footerLeft">
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={handleUpload}
      >
        <PaperClipOutlined className="footerIcon" />
      </Upload>
    </div>
  );

  // 渲染AI生图输入区
  const renderImageInput = () => {
    const currentModel = imageModels.find((m) => m.value === state.imageModel);
    const availableSizes = (currentModel?.sizes || ['1K', '2K', '4K']).map(
      (s) => ({
        label: s,
        value: s,
      }),
    );
    const availableRatios = (
      currentModel?.ratios || ['16:9', '4:3', '1:1', '3:4', '9:16']
    ).map((r) => ({
      label: r,
      value: r,
    }));
    const isGpt55 = currentModel?.value === 'M-0003-0001';

    return (
      <div className="paramRow">
        {renderUpload()}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => {
            state.referenceImageUrl = null;
            state.lastTaskId = null;
            onModeChange?.(v);
          }}
        />
        <DropdownSelect
          options={imageModels}
          value={state.imageModel}
          onChange={(v) => {
            state.referenceImageUrl = null;
            state.lastTaskId = null;
            state.imageModel = v;
          }}
        />
        {/* gpt-5.5 不支持 size，只显示 quality */}
        {isGpt55 ? (
          <DropdownSelect
            options={gptQualityOptions}
            value={state.gptQuality}
            onChange={(v) => (state.gptQuality = v)}
          />
        ) : (
          <>
            <DropdownSelect
              options={availableSizes}
              value={state.imageSize}
              onChange={(v) => (state.imageSize = v)}
            />
            <DropdownSelect
              options={availableRatios}
              value={state.imageRatio}
              onChange={(v) => (state.imageRatio = v)}
            />
          </>
        )}
        <DropdownSelect
          options={imageQuantityOptions}
          value={state.imageQuantity}
          onChange={(v) => (state.imageQuantity = v)}
        />
      </div>
    );
  };

  // 渲染AI视频输入区
  const renderVideoInput = () => {
    const currentModel = videoModels.find((m) => m.value === state.videoModel);
    const availableResolutions = (
      currentModel?.resolutions || ['720p', '1080p']
    ).map((r) => ({
      label: r,
      value: r,
    }));
    const availableRatios = (
      currentModel?.ratios || ['16:9', '4:3', '1:1', '3:4', '9:16']
    ).map((r) => ({
      label: r,
      value: r,
    }));

    return (
      <div className="paramRow">
        {renderUpload()}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => onModeChange?.(v)}
        />
        <DropdownSelect
          options={videoModels}
          value={state.videoModel}
          onChange={(v) => (state.videoModel = v)}
        />
        <DropdownSelect
          options={availableRatios}
          value={state.videoRatio}
          onChange={(v) => (state.videoRatio = v)}
        />
        <DropdownSelect
          options={availableResolutions}
          value={state.videoResolution}
          onChange={(v) => (state.videoResolution = v)}
        />
        <DropdownSelect
          options={videoDurationOptions}
          value={state.videoDuration}
          onChange={(v) => (state.videoDuration = v)}
        />
      </div>
    );
  };

  const placeholderTexts = {
    [MODES.CHAT]: '请输入您的问题或需求...',
    [MODES.IMAGE]: '上传参考图，输入文字，描述你想要生成的图片。',
    [MODES.VIDEO]: '上传参考图，输入文字，描述你想要生成的视频。',
  };

  return (
    <div className="inputArea">
      {/* 引用图区域 - gpt-5.5 引用功能 */}
      {state.referenceImageUrl && (
        <div className="referenceImageArea">
          <span className="referenceImageLabel">
            <LinkOutlined style={{ marginRight: 4 }} />
            引用图片
          </span>
          <div className="referenceImageItem">
            <img src={state.referenceImageUrl} alt="引用图" />
            <div
              className="referenceImageRemove"
              onClick={() => {
                state.lastTaskId = null;
                state.referenceImageUrl = null;
              }}
            >
              <CloseOutlined />
            </div>
          </div>
        </div>
      )}

      {/* 参考图区域 - 上传/添加对话 */}
      {state.images.length > 0 && (
        <div className="uploadedImages">
          {state.images.map((img, idx) => (
            <div key={idx} className="uploadedImgItem">
              <img src={img} alt="" />
              <div
                className="uploadedImgRemove"
                onClick={() => removeImage(idx)}
              >
                <CloseOutlined />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 文本输入 */}
      <Input.TextArea
        ref={textareaRef}
        value={state.text}
        onChange={(e) => (state.text = e.target.value)}
        // onKeyDown={handleKeyDown}
        placeholder={placeholderTexts[mode]}
        className="textarea"
        autoSize={{ minRows: 2, maxRows: 6 }}
        bordered={false}
      />

      <div className="inputWrapper">
        {/* 参数选择行 */}
        {mode === MODES.CHAT && renderChatInput()}
        {mode === MODES.IMAGE && renderImageInput()}
        {mode === MODES.VIDEO && renderVideoInput()}
        <div className="footerRight">
          <Button
            type="primary"
            onClick={handleSend}
            loading={loading}
            disabled={
              loading ||
              (!state.text.trim() &&
                state.images.length === 0 &&
                state.rawImages.length === 0)
            }
            className="sendBtn"
          >
            {!loading && <SendOutlined />}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default InputArea;
